/**
 * The sign-in pseudo-gate on a page's vocabulary downloads.
 *
 * `/cidan/hsk-3/` publishes the whole band as a PDF and as a CSV. A reader
 * the page can see is signed out gets both links turned off and one line under
 * them offering an account, and a reader with an account gets the two links.
 *
 * **A pseudo-gate is all this is, and all it is meant to be.** Every page here
 * is static and cached at the edge, so the markup that reaches a signed-out
 * reader is the markup that reaches everybody, addresses included. Anyone
 * holding one of those addresses fetches the file, `curl` fetches it, and the
 * `Dataset` block a search engine reads describes it in full. What this changes
 * is the one path a person in a browser actually takes, and that is the path
 * worth asking on.
 *
 * **The href stays on the link.** Taking it off would make the address
 * unreadable to a reader who wanted to pass it to somebody, and passing it on
 * is a thing these files are for. `aria-disabled` and a prevented click are
 * what turn the link off, and both leave the address where a reader can find
 * it. See `partials/levels/downloads.html` for the other half of the argument.
 *
 * **The gate closes on a positive answer and never on silence.** A page whose
 * head script was blocked carries no session state at all, and the links are
 * left alone there. The cost of the other way round is a reader who has paid
 * for an account, or who has one already, being shown a dead link and told to
 * sign in for something they are signed in for.
 *
 * `src/site/sign-in-link.ts` puts the current page on the offer's link, so the
 * account a reader makes here comes back to the listing they made it on.
 */

/** The download links, which are the two the panel draws. */
const linkSelector = ".cidian-download-link";

/** The line under them, `hidden` in the markup until a reader needs it. */
const offerSelector = ".cidian-download-offer";

/** What the offer answers, which is why each link points at it. */
const offerId = "cidian-download-offer";

/**
 * What `head/session-state.html` writes on the document for a reader carrying
 * no session, read here as a data attribute.
 *
 * That script reads `__Host-session-hint` before the first paint and writes
 * `in` or `out`. Any other value is a page it never ran on.
 */
const signedOut = "out";

/**
 * Turns the two downloads off for a signed-out reader and offers them an
 * account.
 *
 * Returns on a page with no panel on it, which is every page of this site but
 * the thirteen 词典 level listings.
 * @param document - The page the bundle is running on.
 */
export function startDownloadOffer(document: Document): void {
  const offer = document.querySelector<HTMLElement>(offerSelector);

  if (
    offer === null ||
    document.documentElement.dataset["cbSession"] !== signedOut
  ) {
    return;
  }

  for (const link of document.querySelectorAll<HTMLAnchorElement>(
    linkSelector,
  )) {
    link.setAttribute("aria-disabled", "true");
    link.setAttribute("aria-describedby", offerId);
    link.addEventListener("click", (event) => {
      event.preventDefault();
    });
  }

  offer.hidden = false;
}
